/**
 * pushScheduler - Web Push (VAPID) + local-tab fallback.
 *
 * Strategy:
 *  - Register /sw.js
 *  - Ask Notification permission
 *  - Subscribe to PushManager with VAPID public key from /api/notifications/push/public_key
 *  - POST subscription to /api/notifications/push/subscribe (so backend can deliver)
 *  - Backend's loop pushes to the SW. SW shows native notifications even when tab closed.
 *  - As a foreground fallback (for "now-ish" reminders), still poll /api/notifications/upcoming
 *    and use setTimeout → sw.postMessage("SHOW") in case the backend loop is slow.
 */
import api from "./api";

const TIMERS = new Map();
let pollTimer = null;
let registration = null;

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) out[i] = raw.charCodeAt(i);
  return out;
}

export async function registerServiceWorker() {
  if (!("serviceWorker" in navigator)) return null;
  if (registration) return registration;
  try {
    registration = await navigator.serviceWorker.register("/sw.js");
    await navigator.serviceWorker.ready;
    return registration;
  } catch (e) {
    console.warn("SW register failed", e);
    return null;
  }
}

export async function requestNotificationPermission() {
  if (!("Notification" in window)) return "unsupported";
  if (Notification.permission === "granted") return "granted";
  if (Notification.permission === "denied") return "denied";
  return await Notification.requestPermission();
}

async function subscribeForPush() {
  if (!registration || !("PushManager" in window)) return null;
  try {
    let sub = await registration.pushManager.getSubscription();
    if (!sub) {
      const r = await api.get("/notifications/push/public_key");
      const key = r.data?.public_key;
      if (!key) {
        console.warn("No VAPID public key returned by backend");
        return null;
      }
      sub = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(key),
      });
    }
    const json = sub.toJSON();
    await api.post("/notifications/push/subscribe", {
      endpoint: json.endpoint,
      keys: json.keys,
      user_agent: navigator.userAgent,
    });
    return sub;
  } catch (e) {
    console.warn("Push subscribe failed", e);
    return null;
  }
}

async function scheduleLocal(notif) {
  const when = new Date(notif.scheduled_for).getTime();
  const delay = when - Date.now();
  if (delay < 0 || delay > 24 * 60 * 60 * 1000) return;
  if (TIMERS.has(notif.id)) return;
  const t = setTimeout(() => {
    TIMERS.delete(notif.id);
    if (!registration || !registration.active) return;
    registration.active.postMessage({
      type: "SHOW",
      title: notif.title,
      body: notif.body || "",
      tag: notif.id,
      url: "/notifications",
    });
  }, delay);
  TIMERS.set(notif.id, t);
}

function clearAll() {
  for (const t of TIMERS.values()) clearTimeout(t);
  TIMERS.clear();
}

export async function startPushScheduler() {
  if (!("Notification" in window) || Notification.permission !== "granted") return;
  await registerServiceWorker();
  await subscribeForPush();
  const poll = async () => {
    try {
      const r = await api.get("/notifications/upcoming");
      for (const n of r.data || []) scheduleLocal(n);
    } catch (e) {
      // Auth failures during logout are normal; anything else means the
      // push pipeline is broken and worth investigating.
      if (e?.response && e.response.status !== 401 && e.response.status !== 403) {
        console.error("[push] upcoming poll failed:", e.response.status, e.message);
      }
    }
  };
  await poll();
  if (pollTimer) clearInterval(pollTimer);
  pollTimer = setInterval(poll, 60_000);
}

export function stopPushScheduler() {
  if (pollTimer) clearInterval(pollTimer);
  pollTimer = null;
  clearAll();
}

export async function sendTestPush() {
  const r = await api.post("/notifications/push/test");
  return r.data;
}
